import { canCreateUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormInputFile } from '@/components/forms/form-input-file.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  createStudentUsersByCsv,
  getDroppedStudents,
  getMinimalSchools,
  getStudents,
  getStudentsNoFutureSessions,
  getStudentsOfSchool,
  getStudentsWithMultipleSchedules,
  getUsers,
} from '@/lib/api-calls.js';
import { csvFormSchema, parseCsvAndUpload, STUDENTS_CSV_TEMPLATE_URL } from '@/lib/csv-utils.js';
import { pluralize } from '@/lib/string-utils.js';
import { PostStudentUsersCSVFormKeys, postStudentUserCSVRow, postStudentUsersCSVBody } from '@airreading/common';
import { DEPRECATED_allPagesModifier } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface CreateStudentsByCsvProps {
  trigger: ReactNode;
  onSuccess?: () => void;
  schoolId?: string;
  from?: 'users' | 'students' | 'school' | 'dropped' | 'multiple-schedule';
}
const EMPTY_ERRORS: string[] = [];
const formSchema = postStudentUsersCSVBody.merge(csvFormSchema);

export const CreateStudentsByCsv = ({ trigger, onSuccess, schoolId, from }: CreateStudentsByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const { mutateAsync } = useMutation({
    mutationFn: createStudentUsersByCsv,
  });

  const { data: allSchoolsResponse, isPending: isLoadingSchools } = useQuery(
    getMinimalSchools(DEPRECATED_allPagesModifier),
  );
  const { actingUser } = useSession();

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile, schoolId } = formData;

    const result = await parseCsvAndUpload({
      file: csvFile[0],
      rowSchema: postStudentUserCSVRow,
      onUpload: async (file) => {
        const uploadForm = new FormData();
        uploadForm.append(PostStudentUsersCSVFormKeys.studentsCSV, file);
        uploadForm.append(PostStudentUsersCSVFormKeys.schoolId, schoolId);
        return mutateAsync(uploadForm);
      },
      onToast: toast,
      onSetErrors: setErrorMessages,
    });

    const { successRowsCount, createdRowsCount, updatedRowsCount, hasErrors } = result;

    if (successRowsCount > 0) {
      const parts: string[] = [];
      if (createdRowsCount && createdRowsCount > 0) {
        parts.push(`${createdRowsCount} ${pluralize(createdRowsCount, 'student', 'students')} created`);
      }
      if (updatedRowsCount && updatedRowsCount > 0) {
        parts.push(`${updatedRowsCount} ${pluralize(updatedRowsCount, 'student', 'students')} updated`);
      }
      const title =
        parts.length > 0
          ? parts.join(', ')
          : `${successRowsCount} ${pluralize(successRowsCount, 'student', 'students')} processed`;
      toast({ title });
      onSuccess?.();

      switch (from) {
        case 'school':
          if (schoolId) {
            queryClient.invalidateQueries(getStudentsOfSchool(schoolId));
          }
          break;
        case 'students':
          queryClient.invalidateQueries(getStudents());
          queryClient.invalidateQueries(getStudentsNoFutureSessions());
          break;
        case 'users':
          queryClient.invalidateQueries(getUsers());
          break;
        case 'dropped':
          queryClient.invalidateQueries(getDroppedStudents());
          break;
        case 'multiple-schedule':
          queryClient.invalidateQueries(getStudentsWithMultipleSchedules());
          break;
        default:
          queryClient.invalidateQueries(getStudents());
          queryClient.invalidateQueries(getStudentsNoFutureSessions());
          break;
      }
    }

    if (!hasErrors) {
      closeModal();
    }

    return successRowsCount;
  };

  if (!canCreateUsers(actingUser)) {
    return null;
  }

  return (
    <FormModal
      modalTitle="Create students by CSV"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
      defaultValues={{
        ...(schoolId && { schoolId }),
      }}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <FormSingleSelect
                form={form}
                name="schoolId"
                label="School"
                options={(allSchoolsResponse?.schools || []).map((school) => school.id)}
                renderOption={(id) =>
                  (allSchoolsResponse?.schools || []).find((school) => school.id === id)?.name || ''
                }
                loading={isLoadingSchools}
                triggerProps={{ disabled: !!schoolId }}
              />

              <FormInputFile
                form={form}
                name="csvFile"
                label="Students CSV"
                inputProps={{
                  accept: '.csv',
                  className: 'w-full',
                }}
              />
              <span>
                See{' '}
                <a
                  href={STUDENTS_CSV_TEMPLATE_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600 hover:underline"
                >
                  template (to update)
                </a>{' '}
                for CSV format. Export the sheet as csv file
              </span>
            </div>
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while parsing CSV</AlertTitle>
                  <AlertDescription className="max-h-[240px] overflow-y-auto">
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
};
